import { useCallback } from "react";
import {
  smartrrCustomerAccountPageInjection,
  smartrrAccountSnippets,
} from "@smartrr/shared/shopifyLiquid/templates";
import { updateCustomerPortalToken, updateAsset } from "@vendor-app/app/AdminRoute/AdminSetupRoute/libs/utils";
import { injectLiquid } from "@smartrr/shared/shopifyLiquid/liquidOperations";
import { useToast } from "@vendor-app/app/_sharedComponents/Toast/ToastProvider";
import { GettingStartedStoreAccess } from "../../store";
import { ISODateString } from "@smartrr/shared/entities/ISODateString";

export const useOnPublishCustomerPortalToTheme = () => {
  const { addToast } = useToast();
  const { update } = GettingStartedStoreAccess.useActions();
  const setup = GettingStartedStoreAccess.useSetup();

  return useCallback(async (themeId: number, themeName: string) => {
    try {
      const snippetInjections = [...smartrrCustomerAccountPageInjection, ...smartrrAccountSnippets];

      const responses = await Promise.all([
        updateCustomerPortalToken(themeId),
        ...snippetInjections.map(async ({ name, snippet, replacePosition, toReplace, toRemoveBefore }) => {
          const newValue = injectLiquid(name, "", snippet, toReplace, replacePosition, toRemoveBefore);

          if (newValue === undefined) {
            const error = `Error updating asset ${name}`;
            addToast(error);
            return;
          }

          return updateAsset(themeId, name, newValue);
        }),
      ]);

      const errors = responses.filter(res => !res || res.type === "error");
      if (errors.length) {
        let errorCause;
        if (errors.some(error => error)) {
          errorCause = errors.map(error => (error?.type === "error" && error ? error.message : "")).join(",");
        } else {
          errorCause = "Unable to replace snippet in template";
        }

        const errorMessage = "Error injecting smartrr into one or more liquid files";
        addToast(`${errorMessage}:${errorCause}`);
        return;
      }

      update(
        [
          {
            path: "snippetsThemeId",
            val: themeId.toString(),
          },
          {
            path: "onSiteSetup.smartrrSnippets",
            val: {
              completed: true,
              date: ISODateString.toString(new Date()),
              themeName,
              isBannerDismissed: !!setup?.onSiteSetup?.smartrrSnippets?.isBannerDismissed,
            },
          },
        ],
        "customerPortal",
        { installingSnippets: true }
      );

      addToast("Snippets successfully installed");
    } catch (error) {
      addToast(`Unexpected error installing snippets: ${error?.message ?? error}`);
    }
  }, []);
};
